interface SortableOption {
  label: string;
}

/**
 * Sorts options by prioritizing those that start with the search text,
 * while maintaining original order for same priority level
 */
export const sortOptionsBySearchText = <T extends SortableOption>(
  options: T[],
  searchText: string
): T[] => {
  if (!searchText) return options;

  const searchLower = searchText.toLowerCase();

  // Create array of indices sorted by priority
  const sortedIndices = options
    .map((_, index) => index)
    .sort((aIndex, bIndex) => {
      const a = options[aIndex];
      const b = options[bIndex];

      const aLabelLower = a.label.toLowerCase();
      const bLabelLower = b.label.toLowerCase();

      const aStartsWith = aLabelLower.startsWith(searchLower);
      const bStartsWith = bLabelLower.startsWith(searchLower);
      const aContains = aLabelLower.includes(searchLower);
      const bContains = bLabelLower.includes(searchLower);

      // Priority 1: Options that start with search text
      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;

      // Priority 2: Options that contain search text (but don't start with it)
      if (aContains && !bContains) return -1;
      if (!aContains && bContains) return 1;

      // Priority 3: Keep original order for same priority level
      return aIndex - bIndex;
    });

  // Return options in sorted order
  return sortedIndices.map((index) => options[index]);
};

/**
 * Calculates the new selection when shift-clicking to select a range
 */
export function calculateShiftSelection<T>(
  clickedItem: T,
  allItems: T[],
  currentSelection: T[],
  lastSelectedIndex: number,
  getItemKey: (item: T) => string | null
) {
  const clickedIndex = allItems.findIndex(
    (item) => getItemKey(item) === getItemKey(clickedItem)
  );

  if (lastSelectedIndex >= 0 && clickedIndex >= 0) {
    const start = Math.min(lastSelectedIndex, clickedIndex);
    const end = Math.max(lastSelectedIndex, clickedIndex);
    const rangeItems = allItems.slice(start, end + 1);

    // Start with current selection, then add all items in range
    const newSelection = [...currentSelection];
    rangeItems.forEach((item) => {
      const itemKey = getItemKey(item);
      if (!newSelection.find((selected) => getItemKey(selected) === itemKey)) {
        newSelection.push(item);
      }
    });

    return { newSelection, newLastSelectedIndex: clickedIndex };
  } else {
    // First selection or no previous selection
    const isAlreadySelected = currentSelection.find(
      (selected) => getItemKey(selected) === getItemKey(clickedItem)
    );

    const newSelection = isAlreadySelected
      ? currentSelection
      : [...currentSelection, clickedItem];

    return { newSelection, newLastSelectedIndex: clickedIndex };
  }
}
